"use client"

import { motion } from "motion/react"
import { ArrowRight, Check, Loader2, Mail, Zap } from "lucide-react"
import { useState, type FormEvent } from "react"

type SubscriptionState = "idle" | "loading" | "success" | "error"

export function Newsletter() {
  const [email, setEmail] = useState("")
  const [state, setState] = useState<SubscriptionState>("idle")
  const [message, setMessage] = useState("")

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email.trim() || state === "loading") return

    setState("loading")
    setMessage("")

    try {
      const response = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          utmSource: "hamidayub.com",
          utmCampaign: "principals_log",
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        setState("error")
        setMessage(data.error || "Something went wrong. Try again.")
        return
      }

      setState("success")
      setMessage(data.message || "Welcome aboard. Check your inbox.")
      setEmail("")
    } catch {
      setState("error")
      setMessage("Network error. Please try again.")
    }
  }

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-premium-violet/[0.03] to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_50%_50%,rgba(120,119,198,0.08),transparent)]" />

      <div className="container max-w-2xl mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
          className="text-center"
        >
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-premium-violet/10 border border-premium-violet/20 mb-6">
            <Zap className="w-5 h-5 text-premium-violet" />
          </div>

          {/* Title */}
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
            The Principal&apos;s Log
          </h3>

          {/* Subtitle */}
          <p className="text-slate-400 text-lg leading-relaxed max-w-lg mx-auto mb-2">
            Architecture decisions, technical debt realities, and engineering perspectives 
            that don&apos;t come from a marketing team.
          </p>
          <p className="text-slate-500 text-sm mb-8">
            Delivered when there&apos;s something worth saying. Not on a schedule.
          </p>

          {/* Form */}
          {state === "success" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center justify-center gap-3 py-4 px-6 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300"
            >
              <Check className="w-5 h-5" />
              <span className="font-medium">{message}</span>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="relative">
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      if (state === "error") setState("idle")
                    }}
                    placeholder="you@company.com"
                    required
                    disabled={state === "loading"}
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-slate-600 focus:outline-none focus:border-premium-violet/40 focus:bg-white/[0.07] transition-all disabled:opacity-50"
                  />
                </div>
                <button
                  type="submit"
                  disabled={state === "loading" || !email.trim()}
                  className="group flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white text-black font-semibold text-sm hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {state === "loading" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Subscribe
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </button>
              </div>

              {/* Error message */}
              {state === "error" && message && (
                <motion.p
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 text-sm text-red-400"
                >
                  {message}
                </motion.p>
              )}
            </form>
          )}

          {/* Trust signal */}
          <p className="mt-6 text-xs text-slate-600">
            No spam. Unsubscribe anytime. Your email stays private.
          </p>
        </motion.div>
      </div>
    </section>
  )
}
